import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Notification } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params: { type?: string; read?: boolean; page?: number; limit?: number } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.notifications.list, { params });
      return (response.data.data || response.data) as Notification[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch notifications'));
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.notifications.unreadCount);
      return (response.data.data?.count ?? response.data.count ?? 0) as number;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch unread count'));
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markNotificationRead',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.notifications.markRead(id));
      return (response.data.data || response.data) as Notification;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to mark notification as read'));
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await api.put(endpoints.notifications.markAllRead);
      return true;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to mark all as read'));
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.notifications.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete notification'));
    }
  }
);

export const clearReadNotifications = createAsyncThunk(
  'notifications/clearRead',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.notifications.clearRead);
      return true;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to clear read notifications'));
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    // Fetch
    builder.addCase(fetchNotifications.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => { state.isLoading = false; state.notifications = action.payload; });
    builder.addCase(fetchNotifications.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => { state.unreadCount = action.payload; });

    // Mark read
    builder.addCase(markNotificationRead.fulfilled, (state, action) => {
      const idx = state.notifications.findIndex(n => n._id === action.payload._id);
      if (idx !== -1) state.notifications[idx] = action.payload;
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    });

    // Mark all read
    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.notifications = state.notifications.map(n => ({ ...n, read: true }));
      state.unreadCount = 0;
    });

    // Delete
    builder.addCase(deleteNotification.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n._id === action.payload);
      state.notifications = state.notifications.filter(n => n._id !== action.payload);
      if (notification && !notification.read) state.unreadCount = Math.max(0, state.unreadCount - 1);
    });

    // Clear read
    builder.addCase(clearReadNotifications.fulfilled, (state) => {
      state.notifications = state.notifications.filter(n => !n.read);
    });
  },
});

export const { clearNotificationError } = notificationsSlice.actions;
export default notificationsSlice.reducer;
